import { FC } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { WorkExperienceSelectField } from '@/componentsV2/molecules/WorkExperienceSelectField'
import {
  CompanySize,
  CurrencyUnit,
  EquityGrant,
  IJobCategoryBase,
  ILocationBase,
  IMarketBase,
  ISkillBase,
  JobTypeOfPosition,
  JobWorkExperience,
  JobWorkingForm,
} from '@/types'
import { JobCategorySearchSelectField } from '@/componentsV2/molecules/JobCategorySearchSelectField'
import { LocationSearchSelectField } from '@/componentsV2/molecules/LocationSearchSelectField'
import { SkillSearchSelectField } from '@/componentsV2/molecules/SkillSearchSelectField'
import { TypeOfPositionMultipleSelectField } from '@/componentsV2/molecules/TypeOfPositionMultipleSelectField'
import { SalaryAmountSelectField } from '@/componentsV2/molecules/SalaryAmountSelectField'
import { WorkingFormMultipleSelectField } from '@/componentsV2/molecules/WorkingFormMultipleSelectField'
import { EquityMultipleSelectField } from '@/componentsV2/molecules/EquityMultipleSelectField'
import { getValidEquityGrants } from '@/utils'
import { KeywordSearchGroupFormForJobs } from '@/componentsV2/molecules/KeywordSearchGroupFormForJobs'
import { RoundsMultipleSelectField } from '@/componentsV2/molecules/RoundsMultipleSelectField'
import { MarketSearchSelectField } from '@/componentsV2/molecules/MarketSearchSelectField'
import { CompanySizeSelectField2 } from '@/componentsV2/molecules/CompanySizeSelectField2'
import styles from './index.module.scss'

export type FetchCompaniesWithJobsValues = {
  keyword: string
  markets: string[]
  companySize: CompanySize
  companyLocations: string[]
  roundIds: string[]
  unit: CurrencyUnit
  maxSalary: string
  minSalary: string
  equity: boolean[]
  position: JobTypeOfPosition[]
  experience: JobWorkExperience
  form: JobWorkingForm[]
  jobCategories: string[]
  skills: string[]
  jobLocations: string[]
}

type Props = {
  search: (input: FetchCompaniesWithJobsValues) => Promise<void>
}

type Value = {
  keyword: string
  markets: IMarketBase[]
  companySize: CompanySize
  companyLocations: ILocationBase[]
  roundIds: string[]
  unit: CurrencyUnit
  salaryAmount: {
    maxSalary?: string
    minSalary?: string
  }
  equity: EquityGrant[]
  typeOfPosition: JobTypeOfPosition[]
  workExperience: JobWorkExperience
  workingForm: JobWorkingForm[]
  jobCategories: IJobCategoryBase[]
  skills: ISkillBase[]
  jobLocations: ILocationBase[]
}

export const KeywordSearchGroupForCompaniesWithJobs: FC<Props> = observer(({ search }) => {
  const { t } = useTranslation(['jobs', 'search', 'p', 'validation'])

  const initialValue: Value = {
    keyword: '',
    markets: [],
    companySize: undefined,
    companyLocations: [],
    roundIds: [],
    unit: CurrencyUnit.USD,
    salaryAmount: undefined,
    equity: [],
    typeOfPosition: [],
    workExperience: undefined,
    workingForm: [],
    jobCategories: [],
    skills: [],
    jobLocations: [],
  }

  const validationSchema = Yup.object().shape({})

  const setSearch = async (values: Value) => {
    await search({
      keyword: values.keyword,
      markets: values.markets.map((market) => market.id),
      companySize: values.companySize ? values.companySize : null,
      companyLocations: values.companyLocations.map((location) => location.id),
      roundIds: values.roundIds,
      unit: values.unit,
      maxSalary: values.salaryAmount?.maxSalary,
      minSalary: values.salaryAmount?.minSalary,
      equity: getValidEquityGrants(values.equity),
      position: values.typeOfPosition ? values.typeOfPosition : [],
      experience: values.workExperience ? values.workExperience : null,
      form: values.workingForm ? values.workingForm : [],
      jobCategories: values.jobCategories.map((jobCategory) => jobCategory.id),
      skills: values.skills.map((skill) => skill.id),
      jobLocations: values.jobLocations.map((location) => location.id),
    })
  }

  return (
    <div className='container'>
      <Formik
        initialValues={initialValue}
        validationSchema={validationSchema}
        onSubmit={(values: Value, actions) => {
          setSearch(values)
          actions.setSubmitting(false)
        }}
      >
        {({ setFieldValue, isSubmitting, submitForm, values }) => {
          const setJobCategories = (v: IJobCategoryBase[]) => {
            setFieldValue('jobCategories', v)
            submitForm()
          }
          const setCompanyLocations = (v: ILocationBase[]) => {
            setFieldValue('companyLocations', v)
            submitForm()
          }
          const setJobLocations = (v: ILocationBase[]) => {
            setFieldValue('jobLocations', v)
            submitForm()
          }
          const setSkills = (v: ISkillBase[]) => {
            setFieldValue('locations', v)
            submitForm()
          }
          const setTypeOfPositions = (v: JobTypeOfPosition[]) => {
            setFieldValue('typeOfPosition', v)
            submitForm()
          }
          const setWorkExperience = (v: JobWorkExperience) => {
            setFieldValue('workExperience', v)
            submitForm()
          }
          const setSalaryAmount = (v: { maxSalary?: string; minSalary?: string }) => {
            setFieldValue('salaryAmount', v)
            submitForm()
          }
          const setWorkingForm = (v: JobWorkingForm[]) => {
            setFieldValue('workingForm', v)
            submitForm()
          }
          const setEquity = (v: EquityGrant[]) => {
            setFieldValue('equity', v)
            submitForm()
          }
          const setRoundIds = (v: string[]) => {
            setFieldValue('roundIds', v)
            submitForm()
          }
          const setMarkets = (v: IMarketBase[]) => {
            setFieldValue('markets', v)
            submitForm()
          }
          const setCompanySize = (v: CompanySize) => {
            setFieldValue('companySize', v)
            submitForm()
          }

          return (
            <KeywordSearchGroupFormForJobs isSubmitting={isSubmitting}>
              <div className={styles.container}>
                <MarketSearchSelectField
                  placeholder={t('業界')}
                  selectedMarkets={values.markets}
                  setFieldValue={setMarkets}
                />
                <CompanySizeSelectField2
                  placeholder={t('会社規模')}
                  selectedCompanySize={values.companySize}
                  setFieldValue={setCompanySize}
                />
                <LocationSearchSelectField
                  placeholder={t('会社の所在地')}
                  selectedLocations={values.companyLocations}
                  setFieldValue={setCompanyLocations}
                />
                <RoundsMultipleSelectField
                  placeholder={t('ラウンド')}
                  selectedRoundIds={values.roundIds}
                  setFieldValue={setRoundIds}
                />
                <TypeOfPositionMultipleSelectField
                  placeholder={t('募集形態')}
                  selectedTypeOfPositions={values.typeOfPosition}
                  setFieldValue={setTypeOfPositions}
                />
                <WorkingFormMultipleSelectField
                  placeholder={t('リモートの可否')}
                  selectedWorkingForm={values.workingForm}
                  setFieldValue={setWorkingForm}
                />
                <JobCategorySearchSelectField
                  placeholder={t('職種')}
                  selectedJobCategories={values.jobCategories}
                  setFieldValue={setJobCategories}
                />
                <EquityMultipleSelectField
                  placeholder={t('エクイティまたは SO の有無')}
                  selectedEquity={values.equity}
                  setFieldValue={setEquity}
                />
                <SalaryAmountSelectField
                  placeholder={t('月収')}
                  selectedValue={values.salaryAmount}
                  setFieldValue={setSalaryAmount}
                />
                <LocationSearchSelectField
                  placeholder={t('募集の所在地')}
                  selectedLocations={values.jobLocations}
                  setFieldValue={setJobLocations}
                />
                <WorkExperienceSelectField
                  placeholder={t('経験年数')}
                  selectedValue={values.workExperience}
                  setFieldValue={setWorkExperience}
                />
                <SkillSearchSelectField
                  placeholder={t('スキル')}
                  selectedSkills={values.skills}
                  setFieldValue={setSkills}
                />
              </div>
            </KeywordSearchGroupFormForJobs>
          )
        }}
      </Formik>
    </div>
  )
})
